// src/services/auth.js
//
// 🟢 SIGNING IN WITHOUT THE PHONE IN YOUR HAND.
//
// The browser could only ever get in by QR — which is useless if the phone is flat, at
// home, or being replaced. This is the same account flow the app runs, talking to the
// same endpoints, so a password that works on the handset works here and a reset done
// here is a reset done everywhere:
//
//   sign in          Firebase Auth signInWithEmailAndPassword   (identical to AuthScreen)
//   forgot password  POST /send-reset-code → POST /confirm-password-reset
//   email code       POST /send-verification-email → POST /verify-email-code
//   approval         users/{uid}/login_requests/{id} + POST /api/notify-login-request
//
// 🟥 SIGNING IN IS NOT THE SAME AS BEING LET IN. Firebase hands us a session the instant
//    the password checks out; the account's own rule is that an unrecognised device has
//    to be approved from a trusted one first. That gate lives in App.jsx and is fed by
//    createLoginRequest/watchLoginRequest below — never treat a successful password as
//    admission on its own.

import {
  signInWithEmailAndPassword, signOut,
} from 'firebase/auth';
import {
  doc, setDoc, updateDoc, deleteDoc, onSnapshot, serverTimestamp,
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { BACKEND_URL } from './socket';
import { myDeviceId, deviceLabel, resolveLocation, locationLabel } from './session';

/* ── copy, kept word-for-word in step with the app ────────────────────────── */
export const MSG = {
  invalidCredentials: 'The email or password you entered is incorrect.',
  invalidEmail: 'Please enter a valid email address.',
  genericAuth: 'Email or password is incorrect. Please try again.',
  missingFields: 'Please fill in all fields.',
  tooMany: 'Too many attempts. Please wait a minute and try again.',
  network: 'Network error. Please check your connection and try again.',
  requirements: 'Please make sure your password meets all requirements.',
  codeMissing: 'Please enter the code.',
  codeBad: 'Invalid or expired code. Please try again.',
};

const jsonPost = async (path, body) => {
  const res = await fetch(`${BACKEND_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  let data = {};
  try { data = await res.json(); } catch { /* server returned no body */ }
  return { ok: res.ok, data };
};

/* ── password sign-in ─────────────────────────────────────────────────────── */

export async function loginWithPassword(email, password) {
  if (!email || !password) return { ok: false, error: MSG.missingFields };
  try {
    const cred = await signInWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
    return { ok: true, user: cred.user };
  } catch (e) {
    const code = e?.code || '';
    if (code === 'auth/invalid-email') return { ok: false, error: MSG.invalidEmail };
    if (code === 'auth/too-many-requests') return { ok: false, error: MSG.tooMany };
    if (code === 'auth/network-request-failed') return { ok: false, error: MSG.network };
    // Firebase collapses "no such user" and "wrong password" into
    // auth/invalid-credential on newer SDKs, and that is the RIGHT answer to show —
    // telling a stranger which half was wrong is an account-enumeration oracle.
    if (code === 'auth/user-not-found' || code === 'auth/wrong-password' ||
        code === 'auth/invalid-credential') return { ok: false, error: MSG.invalidCredentials };
    return { ok: false, error: MSG.genericAuth };
  }
}

/* ── forgot password (the app's three steps, same endpoints) ──────────────── */

/** Mirrors AuthScreen.validatePassword — the server enforces none of this. */
export function passwordChecks(pw, confirm) {
  return {
    length: (pw || '').length >= 8,
    capital: /[A-Z]/.test(pw || ''),
    special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pw || ''),
    match: !!pw && pw === confirm,
  };
}
export const passwordOk = (pw, confirm) =>
  Object.values(passwordChecks(pw, confirm)).every(Boolean);

export async function sendResetCode(email) {
  try {
    const { data } = await jsonPost('/send-reset-code', { email: (email || '').trim().toLowerCase() });
    // The server answers success even for an unknown address, on purpose.
    return data?.success ? { ok: true } : { ok: false, error: data?.error || 'Failed to send code.' };
  } catch { return { ok: false, error: MSG.network }; }
}

export async function confirmPasswordReset(email, code, newPassword) {
  try {
    const { data } = await jsonPost('/confirm-password-reset', {
      email: (email || '').trim().toLowerCase(), code: (code || '').trim(), newPassword,
    });
    return data?.success ? { ok: true } : { ok: false, error: data?.error || MSG.codeBad };
  } catch { return { ok: false, error: MSG.network }; }
}

/* ── email code (the approval fallback) ───────────────────────────────────── */

export async function sendEmailCode(email) {
  try {
    const { data } = await jsonPost('/send-verification-email', { email: (email || '').trim().toLowerCase(), purpose: 'login' });   // ⚖️ a sign-in code, said so
    return data?.success ? { ok: true } : { ok: false, error: data?.error || 'Failed to send the code.' };
  } catch { return { ok: false, error: MSG.network }; }
}

export async function verifyEmailCode(email, code) {
  if (!code) return { ok: false, error: MSG.codeMissing };
  try {
    const { data } = await jsonPost('/verify-email-code', {
      email: (email || '').trim().toLowerCase(), code: (code || '').trim(),
    });
    return data?.success ? { ok: true } : { ok: false, error: data?.error || MSG.codeBad };
  } catch { return { ok: false, error: MSG.network }; }
}

/* ── "is this you?" on your phone ─────────────────────────────────────────── */

/**
 * 🟥 RESOLVE THE LOCATION BEFORE WRITING THE DOC, NOT AFTER. The phone's approval sheet
 *    (LoginApprovalHost) deliberately ignores any request whose city still reads
 *    "Fetching location…" so it never shows a half-built card — the app writes the
 *    placeholder first and patches it, and eats the race. We simply don't create the
 *    request until we know where we are, so the sheet appears complete, first time.
 */
export async function createLoginRequest(uid) {
  const deviceId = await myDeviceId();
  const deviceName = deviceLabel();
  const location = await resolveLocation();
  /* 🟥 ONE PENDING REQUEST PER BROWSER, NOT ONE PER ATTEMPT. This was
     `login_${uid}_${Date.now()}`, so every page load, every "try again", every
     reload of the login screen wrote ANOTHER pending document — and nothing
     removed the old ones. LoginPending's unmount cleanup only unsubscribes; it
     does not abandon the request. They accumulated until each went stale five
     minutes later, and the phone was left holding a stack of live approval
     prompts for one person trying to sign in once.

     A stable per-device id makes that impossible rather than tidied up
     afterwards: a retry OVERWRITES the same document (setDoc replaces it and the
     timestamp refreshes), so this browser can never have more than one request
     outstanding. Two different browsers still get two documents, which is
     correct — they are two different logins to approve. */
  const id = `login_${uid}_${deviceId}`;

  await setDoc(doc(db, 'users', uid, 'login_requests', id), {
    deviceName,
    deviceId,
    platform: 'web',
    status: 'pending',
    timestamp: serverTimestamp(),
    created: new Date().toISOString(),
    location,                       // { latitude, longitude, city, country }
  });

  // Wake the trusted device. Fire-and-forget: the phone also has a live Firestore
  // listener, so a failed push delays the sheet, it doesn't prevent it.
  fetch(`${BACKEND_URL}/api/notify-login-request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      userId: uid, deviceName, location: locationLabel(location), requestId: id,
    }),
  }).catch(() => {});

  return { id, deviceId, deviceName, location };
}

/** status: 'pending' | 'approved' | 'declined' */
export function watchLoginRequest(uid, requestId, onChange) {
  return onSnapshot(doc(db, 'users', uid, 'login_requests', requestId), (snap) => {
    if (!snap.exists()) return;
    const d = snap.data() || {};
    onChange?.(d.status || 'pending', d);
  }, () => {});
}

/** Approved by email code instead of by another device — record it the way the app does. */
export async function selfApproveRequest(uid, requestId) {
  try {
    await updateDoc(doc(db, 'users', uid, 'login_requests', requestId), {
      status: 'approved', trusted: true, method: 'email_fallback',
      respondedAt: serverTimestamp(),
    });
  } catch { /* the row is a courtesy record; the session doc is what counts */ }
}

export async function cancelLoginRequest(uid, requestId) {
  try { await deleteDoc(doc(db, 'users', uid, 'login_requests', requestId)); } catch { /* ignore */ }
}

export async function abandonLogin(uid, requestId) {
  if (uid && requestId) await cancelLoginRequest(uid, requestId);
  await signOut(auth).catch(() => {});
}
